import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  X, Info, CalendarDays, GitBranch, Pill, Package, Wallet, Users, ShoppingCart, Truck,
  CreditCard, Briefcase, FileText, Beef, Syringe, Scale, Handshake, Plane, Ticket, Smartphone, Bell,
  MonitorSmartphone,
} from "lucide-react";

const SYSTEMS = {
  consultorio: [
    { id: "agenda", name: "Agenda del día", icon: CalendarDays, type: "table", expl: "Aquí se separan, confirman y agendan las citas. Cada fila es una cita del día.", kpis: [{ l: "Hoy", v: "8" }, { l: "Semana", v: "32" }], header: ["Hora", "Paciente", "Tipo", "Estado"], rows: [["08:30", "María López", "Control", "Confirmada"], ["09:15", "Jorge Andino", "Primera vez", "Confirmada"], ["10:00", "Ana Ruiz", "Consulta", "En espera"], ["10:45", "Pedro Soto", "Control", "Pendiente"]] },
    { id: "recepcion", name: "Recepción y sala de espera", icon: MonitorSmartphone, type: "reception", expl: "La recepcionista gestiona los turnos del día y la pantalla de la sala llama a cada paciente automáticamente, sin gritos ni confusión.", reception: {
      now: { code: "A-08", name: "María López", doctor: "Dra. Paz · Consultorio 1" },
      waiting: [["A-09", "Jorge Andino", "En espera"], ["A-10", "Ana Ruiz", "En espera"], ["A-11", "Pedro Soto", "Confirmado"], ["B-02", "Lucía Vera", "Laboratorio"]],
      called: ["A-07 · Carlos Mena", "A-06 · Rosa Mena", "A-05 · Luis Paredes"],
    } },
    { id: "flujo", name: "Cadena de una cita", icon: GitBranch, type: "flow", expl: "El recorrido completo de una cita, automático de principio a fin.", steps: [
      { t: "Separar la cita", d: "El paciente pide hora desde la página o WhatsApp; el sistema muestra solo horarios disponibles." },
      { t: "Agendar y notificar", d: "La cita se confirma al instante y el paciente recibe un recordatorio por WhatsApp 24 horas antes." },
      { t: "Consulta", d: "El doctor atiende con el historial del paciente a la mano y registra sus notas." },
      { t: "Receta digital", d: "Si aplica, la receta se genera en el sistema y se envía al paciente por WhatsApp o correo." },
      { t: "Cobro", d: "Se registra el pago, se emite el comprobante y queda guardado en el historial." },
    ] },
    { id: "recetas", name: "Recetas digitales", icon: Pill, type: "table", expl: "Las recetas se crean con plantillas y se envían al paciente con un clic.", kpis: [{ l: "Enviadas (semana)", v: "21" }], header: ["Receta", "Paciente", "Fecha", "Estado"], rows: [["R-118", "María López", "17/09", "Enviada"], ["R-119", "Jorge Andino", "17/09", "Enviada"], ["R-120", "Ana Ruiz", "18/09", "Borrador"]] },
    { id: "inventario", name: "Inventario de medicinas", icon: Package, type: "table", expl: "Control de existencias del consultorio con alertas de stock bajo.", kpis: [{ l: "Items", v: "84" }, { l: "Stock bajo", v: "3" }], header: ["Medicina", "Stock", "Estado"], rows: [["Paracetamol 500mg", "120", "OK"], ["Ibuprofeno 400mg", "45", "OK"], ["Amoxicilina 500mg", "8", "Bajo"], ["Suero oral", "15", "OK"]] },
    { id: "cobros", name: "Cobros", icon: Wallet, type: "table", expl: "Registro de pagos por consulta con comprobante incluido.", kpis: [{ l: "Hoy", v: "$320" }, { l: "Mes", v: "$6.480" }], header: ["Factura", "Paciente", "Valor", "Estado"], rows: [["F-340", "María López", "$40", "Pagada"], ["F-341", "Jorge Andino", "$55", "Pagada"], ["F-342", "Ana Ruiz", "$40", "Pendiente"]] },
    { id: "pacientes", name: "Pacientes", icon: Users, type: "table", expl: "Historial y seguimiento de cada paciente en un solo lugar.", kpis: [{ l: "Activos", v: "148" }, { l: "Nuevos (mes)", v: "12" }], header: ["Paciente", "Última visita", "Estado"], rows: [["María López", "10/09", "Activo"], ["Jorge Andino", "08/09", "En tratamiento"], ["Ana Ruiz", "01/09", "Activo"]] },
  ],
  tienda: [
    { id: "pedidos", name: "Pedidos", icon: ShoppingCart, type: "table", expl: "Todos los pedidos llegan aquí, vengan de la página o de WhatsApp.", kpis: [{ l: "Hoy", v: "6" }, { l: "Semana", v: "41" }], header: ["Pedido", "Cliente", "Total", "Estado"], rows: [["#1042", "Rosa Mena", "$36", "Entregado"], ["#1043", "Juan Pérez", "$52", "En camino"], ["#1044", "Lucía Vera", "$28", "Preparación"], ["#1045", "Ana Ruiz", "$64", "Nuevo"]] },
    { id: "flujo", name: "Cadena de compra y pago", icon: GitBranch, type: "flow", expl: "De la compra en línea a la entrega, sin llamadas ni papel.", steps: [
      { t: "Catálogo y pedido", d: "El cliente elige productos en la página y confirma su pedido." },
      { t: "Pago en línea", d: "Paga con tarjeta o transferencia; el sistema confirma el pago automáticamente." },
      { t: "Preparación", d: "El pedido pasa a preparación y el stock se descuenta solo." },
      { t: "Despacho", d: "Se asigna el reparto y se agenda la entrega por zona." },
      { t: "Entrega y confirmación", d: "El cliente recibe la notificación y confirma que recibió su pedido." },
    ] },
    { id: "stock", name: "Stock", icon: Package, type: "table", expl: "Inventario que se actualiza solo con cada venta.", kpis: [{ l: "Productos", v: "64" }, { l: "Agotados", v: "2" }], header: ["Producto", "Stock", "Estado"], rows: [["Ramo rosas x12", "18", "OK"], ["Arreglo primavera", "9", "OK"], ["Tulipanes x10", "0", "Agotado"], ["Girasoles x6", "12", "OK"]] },
    { id: "entregas", name: "Entregas agendadas", icon: Truck, type: "table", expl: "Repartos programados por zona y fecha.", kpis: [{ l: "Mañana", v: "4" }], header: ["Pedido", "Zona", "Fecha", "Estado"], rows: [["#1045", "Cumbayá", "18/09", "Asignado"], ["#1046", "Centro", "18/09", "En ruta"], ["#1047", "El Valle", "19/09", "Programado"]] },
    { id: "pagos", name: "Pagos en línea", icon: CreditCard, type: "table", expl: "Pagos con tarjeta o transferencia confirmados al instante.", kpis: [{ l: "Mes", v: "$4.920" }], header: ["Pedido", "Método", "Valor", "Estado"], rows: [["#1042", "Tarjeta", "$36", "Confirmado"], ["#1043", "Transferencia", "$52", "Confirmado"], ["#1044", "Tarjeta", "$28", "Pendiente"]] },
    { id: "clientes", name: "Clientes", icon: Users, type: "table", expl: "Quién compra, cuánto gasta y cada cuánto vuelve.", kpis: [{ l: "Total", v: "312" }, { l: "Frecuentes", v: "58" }], header: ["Cliente", "Última compra", "Total"], rows: [["Rosa Mena", "12/09", "$86"], ["Juan Pérez", "09/09", "$42"], ["Lucía Vera", "02/09", "$120"]] },
  ],
  servicios: [
    { id: "proyectos", name: "Proyectos", icon: Briefcase, type: "table", expl: "Cada trabajo con su cliente, estado y responsable.", kpis: [{ l: "Activos", v: "7" }, { l: "Este mes", v: "3" }], header: ["Proyecto", "Cliente", "Estado"], rows: [["Rediseño web", "Comercial Sur", "En curso"], ["Capacitación", "Grupo Andino", "Planificado"], ["Auditoría", "TecnoEC", "Entregado"]] },
    { id: "flujo", name: "Cadena de un servicio", icon: GitBranch, type: "flow", expl: "De la cotización al cobro, con todo registrado.", steps: [
      { t: "Cotización", d: "Se genera y se envía al cliente desde el sistema." },
      { t: "Aprobación", d: "El cliente acepta y el proyecto se activa automáticamente." },
      { t: "Ejecución", d: "El equipo registra avances y tiempos por tarea." },
      { t: "Entrega", d: "Se entrega con acta y el cliente confirma conformidad." },
      { t: "Cobro", d: "Se emite la factura y se hace seguimiento al pago." },
    ] },
    { id: "cotizaciones", name: "Cotizaciones", icon: FileText, type: "table", expl: "Cotizaciones con estado y seguimiento.", kpis: [{ l: "Abiertas", v: "5" }], header: ["N°", "Cliente", "Valor", "Estado"], rows: [["C-089", "Comercial Sur", "$1.200", "Enviada"], ["C-090", "Grupo Andino", "$850", "Aprobada"], ["C-091", "TecnoEC", "$2.300", "Negociación"]] },
    { id: "facturacion", name: "Facturación", icon: Wallet, type: "table", expl: "Facturas emitidas y cobros pendientes.", kpis: [{ l: "Mes", v: "$12.400" }, { l: "Pendiente", v: "$3.150" }], header: ["Factura", "Cliente", "Estado"], rows: [["F-221", "Comercial Sur", "Pagada"], ["F-222", "TecnoEC", "Pendiente"], ["F-223", "Grupo Andino", "Pagada"]] },
    { id: "clientes", name: "Clientes", icon: Users, type: "table", expl: "Relación con cada cliente y su historial.", kpis: [{ l: "Total", v: "96" }, { l: "Nuevos (mes)", v: "8" }], header: ["Cliente", "Contacto", "Estado"], rows: [["Comercial Sur", "Ana P.", "Activo"], ["Grupo Andino", "Luis M.", "Activo"], ["TecnoEC", "Sofía R.", "Prospecto"]] },
  ],
  ganadero: [
    { id: "lotes", name: "Lotes y animales", icon: Beef, type: "table", expl: "Cada lote con sus cabezas, potrero y estado actual.", kpis: [{ l: "Cabezas", v: "342" }, { l: "Lotes", v: "8" }], header: ["Lote", "Cabezas", "Potrero", "Estado"], rows: [["A-1", "48", "Norte", "Pastoreo"], ["A-2", "35", "Sur", "Pastoreo"], ["C-3", "22", "Corral 2", "Engorde"]] },
    { id: "sanidad", name: "Sanidad y vacunas", icon: Syringe, type: "table", expl: "Registro sanitario con alertas de próximas dosis.", kpis: [{ l: "Al día", v: "96%" }, { l: "Próximas", v: "12" }], header: ["Animal", "Lote", "Tratamiento", "Próx. dosis"], rows: [["Vaca #1042", "A-1", "Fiebre aftosa", "12/10"], ["Toro #208", "A-2", "Brucelosis", "15/10"], ["Vaca #1177", "C-3", "Vitamina ADE", "20/10"]] },
    { id: "produccion", name: "Pesos y producción", icon: Scale, type: "table", expl: "Pesajes y ganancia diaria por animal para decidir cuándo vender.", kpis: [{ l: "Peso prom.", v: "420 kg" }, { l: "Ganancia", v: "0.8 kg/día" }], header: ["Animal", "Peso", "Ganancia/día"], rows: [["#1042", "452 kg", "0.9 kg"], ["#208", "610 kg", "1.1 kg"], ["#1177", "398 kg", "0.7 kg"]] },
    { id: "flujo", name: "Cadena de venta", icon: GitBranch, type: "flow", expl: "Del registro del animal a la venta y el traslado.", steps: [
      { t: "Registro del animal", d: "Cada animal entra al sistema con su arete, lote y genealogía." },
      { t: "Seguimiento sanitario", d: "Vacunas y tratamientos quedan registrados con alertas." },
      { t: "Pesaje", d: "Los pesajes periódicos muestran la ganancia y el momento ideal de venta." },
      { t: "Venta", d: "Se negocia con el comprador y se genera el documento de venta." },
      { t: "Pago y traslado", d: "Se registra el pago y se coordina la guía de traslado." },
    ] },
    { id: "compradores", name: "Compradores", icon: Handshake, type: "table", expl: "Clientes y negociaciones de venta de ganado.", kpis: [{ l: "Activos", v: "24" }], header: ["Comprador", "Tipo", "Última compra"], rows: [["Frigorífico del Valle", "Mayorista", "08/09"], ["Hacienda El Roble", "Cría", "22/08"], ["Mercado Central", "Mayorista", "15/09"]] },
  ],
  turismo: [
    { id: "viajes", name: "Viajes y cronogramas", icon: Plane, type: "table", expl: "Cada salida con sus fechas, cupos y estado.", kpis: [{ l: "Activos", v: "4" }, { l: "Cupos libres", v: "26" }], header: ["Viaje", "Fechas", "Cupos", "Estado"], rows: [["Galápagos 5D", "12–16 Oct", "8/12", "Confirmado"], ["Cuenca Finde", "20–22 Sep", "14/16", "Reservando"], ["Amazonía 4D", "02–05 Nov", "5/10", "Reservando"]] },
    { id: "app", name: "App del viajero", icon: Smartphone, type: "phone", expl: "El cliente sigue su viaje desde su celular: cronograma, horarios y notificaciones en tiempo real.", phone: {
      title: "Mi viaje: Galápagos 5D",
      status: "Día 2 de 5 · En curso",
      items: [
        ["Día 1 · Vuelo y check-in", "Completado", "done"],
        ["Día 2 · Tour Isla Bartolomé", "Hoy · 08:00", "today"],
        ["Día 3 · Snorkel y playa", "Mañana · 09:30", ""],
        ["Día 4 · Tortuga Bay", "15/10 · 08:00", ""],
        ["Día 5 · Retorno a Quito", "16/10 · 15:00", ""],
      ],
      note: "Notificación: tu guía te espera en el lobby a las 07:45.",
    } },
    { id: "reservas", name: "Reservas", icon: Ticket, type: "table", expl: "Reservas con pago y confirmación automática.", kpis: [{ l: "Este mes", v: "38" }], header: ["Cliente", "Viaje", "Pago", "Estado"], rows: [["Familia Cordero", "Galápagos 5D", "Pagado", "Confirmada"], ["María José P.", "Cuenca Finde", "50%", "Parcial"], ["Grupo UDLA", "Amazonía 4D", "Pendiente", "Reservada"]] },
    { id: "flujo", name: "Cadena de un viaje", icon: GitBranch, type: "flow", expl: "De la reserva al regreso, con el viajero siempre informado.", steps: [
      { t: "Reserva en línea", d: "El cliente elige el viaje, las fechas y reserva su cupo." },
      { t: "Pago", d: "Paga en línea o por transferencia; el cupo se confirma solo." },
      { t: "Cronograma enviado", d: "Recibe el itinerario día por día en su celular." },
      { t: "Notificaciones en viaje", d: "Horarios, cambios y avisos llegan automáticamente." },
      { t: "Encuesta final", d: "Al regresar, el viajero califica su experiencia." },
    ] },
    { id: "clientes", name: "Viajeros", icon: Users, type: "table", expl: "Historial de viajes por cliente para ofrecerles el siguiente.", kpis: [{ l: "Total", v: "540" }, { l: "Repiten", v: "31%" }], header: ["Cliente", "Último viaje", "Estado"], rows: [["Familia Cordero", "Galápagos", "Activo"], ["María José P.", "Cuenca", "Frecuente"], ["Carlos Mena", "Baños", "Hace 1 año"]] },
  ],
};

const TableView = ({ mod, color }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    {mod.kpis && (
      <div className="mb-4 flex flex-wrap gap-2">
        {mod.kpis.map((k, i) => (
          <span key={i} className="rounded-md px-3 py-1.5 text-xs font-semibold" style={{ backgroundColor: `${color}14`, color }}>
            {k.l}: <strong>{k.v}</strong>
          </span>
        ))}
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-slate-400">
            {mod.header.map((h, i) => (
              <th key={i} className="pb-2 pr-3 text-xs font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mod.rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-50 text-slate-600 transition-colors hover:bg-slate-50">
              {r.map((c, j) => (
                <td key={j} className="py-2.5 pr-3 text-[13px]">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">Solo lectura · datos de ejemplo</p>
  </div>
);

const FlowView = ({ mod, color }) => (
  <div className="mx-auto max-w-2xl">
    {mod.steps.map((s, i) => (
      <div key={i} className="flex gap-4">
        <div className="flex flex-col items-center">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full font-display text-sm font-bold text-white" style={{ backgroundColor: color }}>
            {i + 1}
          </span>
          {i < mod.steps.length - 1 && <span className="my-1 w-px flex-1" style={{ backgroundColor: `${color}55` }} />}
        </div>
        <div className="pb-7">
          <h4 className="font-display text-base font-bold text-slate-900">{s.t}</h4>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">{s.d}</p>
        </div>
      </div>
    ))}
  </div>
);

const PhoneView = ({ mod, color }) => (
  <div className="flex justify-center py-2">
    <div className="w-[300px] overflow-hidden rounded-[2rem] border-8 border-slate-800 bg-white shadow-2xl">
      <div className="px-5 pb-4 pt-6 text-white" style={{ backgroundColor: color }}>
        <p className="font-display text-sm font-bold">{mod.phone.title}</p>
        <p className="mt-1 text-[11px] opacity-85">{mod.phone.status}</p>
      </div>
      <div className="space-y-2.5 p-4">
        {mod.phone.items.map(([label, time, st], i) => (
          <div key={i} className="flex items-center gap-3">
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${st === "today" ? "animate-pulse" : ""}`}
              style={{ backgroundColor: st ? color : "#CBD5E1" }}
            />
            <div className="flex-1">
              <p className={`text-[12px] font-semibold ${st ? "text-slate-800" : "text-slate-400"}`}>{label}</p>
              <p className="text-[10px] text-slate-400">{time}</p>
            </div>
          </div>
        ))}
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
          <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color }} />
          <p className="text-[11px] text-slate-600">{mod.phone.note}</p>
        </div>
      </div>
    </div>
  </div>
);

const ReceptionView = ({ data, color }) => (
  <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
    <div className="rounded-2xl bg-slate-900 p-8 text-white shadow-xl">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Pantalla de sala · ahora atendiendo</p>
      <p className="mt-5 font-display text-6xl font-black sm:text-7xl" style={{ color }}>{data.now.code}</p>
      <p className="mt-3 font-display text-xl font-bold">{data.now.name}</p>
      <p className="mt-1 text-sm text-slate-400">{data.now.doctor}</p>
      <div className="mt-8 border-t border-white/10 pt-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Llamados recientemente</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.called.map((c, i) => (
            <span key={i} className="rounded-full bg-white/10 px-3 py-1.5 text-xs text-slate-300">{c}</span>
          ))}
        </div>
      </div>
    </div>
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="flex items-center gap-2 font-display text-sm font-bold text-slate-900">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
        Sala de espera
      </h4>
      <div className="mt-4 space-y-2">
        {data.waiting.map(([code, name, status], i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2.5">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs font-bold" style={{ color }}>{code}</span>
              <span className="text-sm text-slate-700">{name}</span>
            </div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">{status}</span>
          </div>
        ))}
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">Solo lectura · datos de ejemplo</p>
    </div>
  </div>
);

export const SystemDemo = ({ demo: d, onClose }) => {
  const modules = SYSTEMS[d.template] || SYSTEMS.servicios;
  const [activeId, setActiveId] = useState(modules[0].id);
  const active = modules.find((m) => m.id === activeId) || modules[0];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[85] flex flex-col bg-slate-100"
      data-testid="system-demo"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white sm:px-6" style={{ backgroundColor: d.primary_color }}>
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold sm:text-base">{d.business_name} — Sistema de gestión</p>
          <p className="text-[11px] opacity-75">Interfaz de referencia · datos de ejemplo</p>
        </div>
        <button
          onClick={onClose}
          data-testid="system-demo-close"
          className="flex shrink-0 items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-xs font-bold backdrop-blur transition-colors hover:bg-white/25"
        >
          Salir <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-[12px] leading-snug text-amber-800">
        Así se vería y se manejaría el sistema. Al adquirir el producto, la interfaz y los módulos se adaptan exactamente a los procesos de tu negocio.
      </div>

      <div className="flex flex-1 flex-col overflow-hidden sm:flex-row">
        <div className="flex gap-1.5 overflow-x-auto border-b border-slate-200 bg-white px-3 py-2 sm:hidden">
          {modules.map((m) => (
            <button
              key={m.id}
              onClick={() => setActiveId(m.id)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${activeId === m.id ? "text-white" : "text-slate-500"}`}
              style={activeId === m.id ? { backgroundColor: d.primary_color } : {}}
            >
              {m.name}
            </button>
          ))}
        </div>

        <aside className="hidden w-60 shrink-0 overflow-y-auto border-r border-slate-200 bg-white sm:block">
          <p className="px-5 pb-2 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Módulos</p>
          {modules.map((m) => (
            <button
              key={m.id}
              data-testid={`system-nav-${m.id}`}
              onClick={() => setActiveId(m.id)}
              className={`flex w-full items-center gap-3 px-5 py-3 text-left text-sm transition-colors ${activeId === m.id ? "font-bold text-slate-900" : "text-slate-500 hover:bg-slate-50"}`}
              style={activeId === m.id ? { borderLeft: `3px solid ${d.primary_color}`, backgroundColor: `${d.primary_color}0D` } : { borderLeft: "3px solid transparent" }}
            >
              <m.icon className="h-4 w-4 shrink-0" style={activeId === m.id ? { color: d.primary_color } : {}} />
              {m.name}
            </button>
          ))}
        </aside>

        <main className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0" style={{ color: d.primary_color }} />
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">{active.name}</h2>
              <p className="mt-1 text-sm text-slate-500">{active.expl}</p>
            </div>
          </div>
          {active.type === "table" && <TableView mod={active} color={d.primary_color} />}
          {active.type === "flow" && <FlowView mod={active} color={d.primary_color} />}
          {active.type === "phone" && <PhoneView mod={active} color={d.primary_color} />}
          {active.type === "reception" && <ReceptionView data={active.reception} color={d.primary_color} />}
        </main>
      </div>
    </motion.div>
  );
};
